import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/features/auth/use-auth';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useAsamblea, useActa } from '@/features/asambleas/api';
import { ActaEditor } from '@/features/asambleas/acta-editor';
import { ActaViewer } from '@/features/asambleas/acta-viewer';
import { PageContainer } from '@/components/common/page-container';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export function ActaRoute() {
  const { asambleaId } = useParams<{ asambleaId: string }>();
  const { session } = useAuth();
  const { condoId, rol } = useCondoContext();

  const { data: asamblea, isLoading: loadingAsamblea } = useAsamblea(condoId, asambleaId);
  const { data: acta, isLoading: loadingActa } = useActa(condoId, asambleaId);

  const isLoading = loadingAsamblea || loadingActa;

  if (isLoading) {
    return (
      <PageContainer width="wide">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-96" />
      </PageContainer>
    );
  }

  if (!asamblea) return null;

  const backLink = (
    <Button variant="ghost" asChild size="sm" className="mb-6">
      <Link to={`/app/condominios/${condoId}/asambleas/${asambleaId}`}>
        <ArrowLeft className="h-4 w-4" />
        Volver a la asamblea
      </Link>
    </Button>
  );

  if (!acta) {
    return (
      <PageContainer width="wide">
        {backLink}
        <div className="mx-auto max-w-md rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-10 text-center">
          <p className="eyebrow">Acta pendiente</p>
          <h1 className="font-display-tight mt-2 text-3xl font-semibold">Acta no generada</h1>
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">
            El acta se genera automaticamente cuando la asamblea cierra la votacion.
            Una vez generada, el administrador y el presidente podran firmarla con FEA.
          </p>
        </div>
      </PageContainer>
    );
  }

  // Dispatch: admin (or super_admin) + borrador → editor; else → viewer
  const isAdmin = rol === 'admin' || rol === 'super_admin';
  const showEditor = isAdmin && acta.estado === 'borrador';

  return (
    <PageContainer width="wide">
      {backLink}
      {showEditor ? (
        <ActaEditor
          condoId={condoId}
          asamblea={asamblea}
          acta={acta}
          sessionUserId={session?.userId ?? ''}
        />
      ) : (
        <ActaViewer condoId={condoId} asamblea={asamblea} acta={acta} />
      )}
    </PageContainer>
  );
}
